import { useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, imgSrc } from "@/lib/api";
import { waLink } from "@/lib/brand";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const field = "w-full rounded-[4px] border border-[#b08a3e]/30 bg-white px-4 py-3 text-sm text-[#1d1a17] placeholder:text-[#8a8077] focus:outline-none focus:border-[#8a6a2c]";

export const MugOrderDialog = ({ mug, onClose }) => {
  const [photo, setPhoto] = useState("");
  const [preview, setPreview] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/public/upload", fd);
      setPhoto(data.url);
    } catch (err) {
      setPreview("");
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const send = () => {
    const lines = [`Olá, Idealize! Quero personalizar a ${mug.name}${mug.price ? ` (R$ ${mug.price})` : ""}.`];
    if (name.trim()) lines.push(`Nome: ${name.trim()}`);
    if (note.trim()) lines.push(`Texto/ideia: ${note.trim()}`);
    if (photo) lines.push(`Minha foto: ${imgSrc(photo)}`);
    window.open(waLink(lines.join("\n")), "_blank", "noopener");
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="mug-order-dialog" className="max-h-[92vh] overflow-y-auto rounded-[6px] border-[#b08a3e]/30 bg-[#f8efe8] text-[#1d1a17] sm:max-w-md">
        <DialogHeader className="text-left">
          <p className="font-jost text-xs tracking-[0.3em] text-[#8a6a2c]">SEU PEDIDO</p>
          <DialogTitle className="font-serif-lux text-3xl font-semibold">{mug.name}</DialogTitle>
          <DialogDescription className="text-sm text-[#4a4440]">Envie a foto que vai na caneca. Ela segue junto com o pedido no WhatsApp.</DialogDescription>
        </DialogHeader>
        <label data-testid="mug-order-photo-drop" className="relative flex aspect-[4/3] cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-[4px] border border-dashed border-[#b08a3e]/50 bg-white/60 text-sm text-[#4a4440] transition-colors hover:bg-white">
          {preview ? (
            <>
              <img src={preview} alt="Sua foto" className="absolute inset-0 h-full w-full object-cover" />
              {busy && <span className="absolute inset-0 grid place-items-center bg-white/60"><Loader2 className="h-6 w-6 animate-spin text-[#8a6a2c]" /></span>}
              {!busy && <button type="button" data-testid="mug-order-photo-remove" onClick={(e) => { e.preventDefault(); setPhoto(""); setPreview(""); }} className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-[#1d1a17] text-white"><X className="h-4 w-4" /></button>}
            </>
          ) : (
            <><ImagePlus className="h-7 w-7 text-[#8a6a2c]" strokeWidth={1.4} /><span className="font-jost tracking-[0.15em] text-xs">TOQUE PARA ENVIAR SUA FOTO</span><span className="text-xs text-[#6b625b]">JPG, PNG ou WEBP · até 8MB</span></>
          )}
          <input data-testid="mug-order-photo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />
        </label>
        <input data-testid="mug-order-name-input" className={field} placeholder="Seu nome" value={name} onChange={(e) => setName(e.target.value)} />
        <textarea data-testid="mug-order-note-input" rows={3} className={field} placeholder="Frase, nome ou ideia para a arte (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
        <button data-testid="mug-order-send-button" disabled={busy} onClick={send} className="w-full rounded-full bg-[#8a6a2c] py-4 font-jost text-xs tracking-[0.25em] text-white transition-colors hover:bg-[#1d1a17] disabled:opacity-60">
          {busy ? "ENVIANDO FOTO..." : "ENVIAR PEDIDO NO WHATSAPP"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
